import { type ReactNode, useEffect } from 'react';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { env } from '@/env';
import { setTokenGetter } from '@/api/http';
import { useTheme } from '@marcianus/design/components';
import { buildClerkAppearance, clerkLocalizationFor } from './clerkAppearance';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@/i18n';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
});

/** Liga o getToken do Clerk ao cliente HTTP (uma vez). */
function TokenBridge({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  useEffect(() => { setTokenGetter(() => getToken()); }, [getToken]);
  // 1.º paint do React → dissolve o splash estático do index.html (o BootGate
  // assume a partir daqui com o MESMO visual — transição sem salto).
  useEffect(() => {
    const el = document.getElementById('initial-splash');
    if (!el) return;
    el.classList.add('is-hiding');
    const t = setTimeout(() => el.remove(), 450);
    return () => clearTimeout(t);
  }, []);
  return <>{children}</>;
}

export function Providers({ children }: { children: ReactNode }) {
  const { theme } = useTheme();
  const { i18n } = useTranslation();
  return (
    <ClerkProvider
      publishableKey={env.clerkPublishableKey}
      afterSignOutUrl="/"
      appearance={buildClerkAppearance(theme)}
      localization={clerkLocalizationFor(i18n.language as Locale) as never}
    >
      <QueryClientProvider client={queryClient}>
        <TokenBridge>{children}</TokenBridge>
      </QueryClientProvider>
    </ClerkProvider>
  );
}
